'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { api } from '@/lib/apiClient';

const BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api/proxy/';

const AdBanner = ({ type, noofads }) => {
    const router = useRouter();
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAds = async () => {
            try {
                setLoading(true);
                let whereClause = '1=1';
                const whereParams = [];
                if (type) {
                    whereClause = 'view_places ? $1';
                    whereParams.push(type);
                }
                whereClause += ` ORDER BY random() limit $${whereParams.length + 1}`;
                whereParams.push(noofads ? parseInt(noofads, 10) : 1);

                console.log("ADWhereClause", whereClause);
                console.log("ADWhereParams", whereParams);

                const adsRes = await api.post('selectwithout/view_home_ad_list', {
                    sqlData: '*',
                    where: whereClause,
                    whereData: whereParams
                });
                console.log("adsRes", adsRes);
                if (adsRes.success === 1 && Array.isArray(adsRes.data)) {
                    let filtered = adsRes.data.filter(ad => {
                        if (!type) return true;
                        console.log("ad", ad);
                        // Check view_places match
                        if (ad.view_places) {
                            try {
                                const vp = typeof ad.view_places === 'string'
                                    ? JSON.parse(ad.view_places)
                                    : ad.view_places;

                                if (Array.isArray(vp) && vp.some(val => val.toLowerCase() === type.toLowerCase())) return true;
                                if (typeof vp === 'object' && vp !== null && vp[type] === true) return true;
                                if (typeof vp === 'string' && vp.toLowerCase().includes(type.toLowerCase())) return true;
                            } catch (e) {
                                if (typeof ad.view_places === 'string' && ad.view_places.toLowerCase().includes(type.toLowerCase())) return true;
                            }
                        }

                        // Check listing_type match
                        if (ad.listing_type && ad.listing_type.toLowerCase() === type.toLowerCase()) {
                            return true;
                        }

                        return false;
                    });

                    // Shuffle
                    filtered.sort(() => 0.5 - Math.random());

                    // Slice to noofads (default to 1)
                    const limit = noofads ? parseInt(noofads, 10) : 1;
                    const selectedAds = filtered.slice(0, limit);

                    setAds(selectedAds);

                    // Track views
                    selectedAds.forEach(async (ad) => {
                        try {
                            const currentViews = ad.views || 0;
                            await api.post(`updatewithout/ad_listing_det/${ad.ad_id}`, { views: currentViews + 1 }, { skipAuthGuard: true });
                        } catch (err) {
                            console.error('Error updating views count:', err);
                        }
                    });
                }
            } catch (err) {
                console.error('Error fetching ads:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchAds();
    }, [type, noofads]);

    const handleAdClick = async (e, ad, link, isExternal) => {
        e.preventDefault();
        try {
            const token = Cookies.get('token');
            if (!token) {
                alert("Please login to click on the ad");
                return;
            }

            let userId = Cookies.get('id');
            if (userId) {
                userId = userId.replace(/^"|"$/g, '');
            }

            let clicksUsersList = [];
            if (ad.clicks_users_list) {
                try {
                    clicksUsersList = typeof ad.clicks_users_list === 'string'
                        ? JSON.parse(ad.clicks_users_list)
                        : ad.clicks_users_list;
                } catch (err) {
                    clicksUsersList = [];
                }
            }
            if (!Array.isArray(clicksUsersList)) {
                clicksUsersList = [];
            }
            if (userId) {
                clicksUsersList.push(userId);
            }

            const currentClicks = ad.clicks || 0;
            await api.post(
                `updatewithout/ad_listing_det/${ad.ad_id}`,
                {
                    clicks: currentClicks + 1,
                    clicks_users_list: clicksUsersList.length > 0 ? JSON.stringify(clicksUsersList) : null
                },
                { skipAuthGuard: true }
            );

            if (isExternal) {
                window.open(link, '_blank', 'noopener,noreferrer');
            } else {
                router.push(link);
            }
        } catch (err) {
            console.error("Error updating clicks:", err);
            // fallback
            if (isExternal) {
                window.open(link, '_blank', 'noopener,noreferrer');
            } else {
                router.push(link);
            }
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col gap-4 w-full">
                {Array.from({ length: noofads || 1 }).map((_, idx) => (
                    <div key={idx} className="animate-pulse bg-slate-100 border border-slate-200 rounded-2xl h-64 w-full flex items-center justify-center">
                        <span className="text-slate-400 text-sm">Loading advertisement...</span>
                    </div>
                ))}
            </div>
        );
    }

    if (ads.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4 w-full">
            {ads.map((ad) => {
                let image = '/no-image.jpeg';
                if (ad.featured_image_url) {
                    if (ad.featured_image_url.startsWith('http') || ad.featured_image_url.startsWith('https') || ad.featured_image_url.startsWith('data:')) {
                        image = ad.featured_image_url;
                    } else {
                        image = `${BASE_URL.replace(/\/$/, '')}/${ad.featured_image_url.replace(/^\//, '')}`;
                    }
                }

                let link = '#';
                let isExternal = false;
                if (ad.listing_type) {
                    if (ad.property_sub_type != 6 && ad.listing_type === 'sell') {
                        link = `/property/${ad.view_id}`;
                    } else {
                        link = `/rent/p/${ad.view_id}`;
                    }
                } else {
                    link = `/projects/p/${ad.view_id}`;
                    isExternal = true;
                }

                const title = ad.ad_head || 'Sponsored Ad';
                const description = ad.short_desc || '';
                const ctaText = 'View Details';

                return (
                    <a
                        key={ad.ad_id}
                        href={link}
                        onClick={(e) => handleAdClick(e, ad, link, isExternal)}
                        className="block w-full"
                    >
                        <div className="group relative overflow-hidden rounded-2xl bg-white shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300 h-full w-full">
                            {/* Image Background */}
                            <div className="relative h-64 overflow-hidden">
                                <img
                                    src={image}
                                    alt={title}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                                {/* Sponsored Tag */}
                                <div className="absolute top-3 right-3">
                                    <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-white/80 bg-black/30 backdrop-blur-sm rounded-md border border-white/10">
                                        Sponsored
                                    </span>
                                </div>

                                {/* Content Overlay */}
                                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                                    <h3 className="text-xl font-bold mb-2 leading-tight">{title}</h3>
                                    <p className="text-sm text-gray-200 mb-4 line-clamp-2">{description}</p>

                                    <div className="w-full py-2.5 bg-white text-slate-900 text-sm font-semibold rounded-lg hover:bg-slate-50 active:scale-[0.98] transition-all text-center">
                                        {ctaText}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </a>
                );
            })}
        </div>
    );
};

export default AdBanner;
